/**
 * Reorders a flat project list so each root is followed by its descendants (DFS).
 * Sibling order follows the original API order when possible.
 */
export function orderProjectsUnderParents(projects) {
  if (!Array.isArray(projects) || projects.length === 0) return projects;

  const byId = new Map(projects.map((p) => [p.id, p]));
  const used = new Set();
  const out = [];
  const apiIndex = new Map(projects.map((p, i) => [p.id, i]));

  const cmpApiOrder = (a, b) =>
    (apiIndex.get(a.id) ?? 0) - (apiIndex.get(b.id) ?? 0);

  const childrenOf = (parentId) =>
    projects.filter((p) => p.parent_project_id === parentId).sort(cmpApiOrder);

  function visit(node) {
    if (used.has(node.id)) return;
    used.add(node.id);
    out.push(node);
    for (const ch of childrenOf(node.id)) visit(ch);
  }

  const roots = projects.filter(
    (p) => !p.parent_project_id || !byId.has(p.parent_project_id)
  );
  roots.sort(cmpApiOrder);
  for (const r of roots) visit(r);

  for (const p of projects) {
    if (!used.has(p.id)) visit(p);
  }

  return out;
}

/** Indentation depth for subprojects (ancestors present on the same list page). */
export function projectParentDepth(item, projectsOnPage) {
  if (!item?.parent_project_id) return 0;
  const byId = new Map((projectsOnPage || []).map((p) => [p.id, p]));
  let depth = 0;
  let cur = item;
  const seen = new Set();
  while (cur?.parent_project_id) {
    if (seen.has(cur.id)) break;
    seen.add(cur.id);
    const parent = byId.get(cur.parent_project_id);
    if (!parent) return Math.max(depth + 1, 1);
    depth += 1;
    cur = parent;
  }
  return depth;
}
